import {WebsiteSettings} from './website-settings';

export const releaseChannels = ['STANDARD', 'BETA'] as const;

export type ReleaseChannel = typeof releaseChannels[number];

export const registrationTypes = ['lead-generation', 'maxi-registration', 'binding-registration'] as const;

export type RegistrationType = typeof registrationTypes[number];

export const registrationTypeConfigs: Record<RegistrationType, {
  title: string
  text: string
  image: string
}> = {
  'lead-generation':
  {
    title: 'Lead Generierung',
    text: 'Sammelt Interessenten-Daten ohne verbindliche Anmeldung. Der Schüler erhält eine Anmeldebestätigung mit Widerrufsbelehrung und muss anschließend manuell im CRM verarbeitet werden.',
    image: '/assets/registration-processes/Lead-Generierung.png'
  },
    'maxi-registration':
  {
    title: 'Maxi Anmeldung',
    text: 'Schließt Anmeldungen im Regelfall vollautomatisch via Maxi ab. Der Schüler erhält eine Anmeldebestätigung mit Widerrufsbelehrung und seine Fahren-Lernen Zugangsdaten vom Vogel-Verlag. Je nach Konfiguration im Fahrschulmanager können einige Anmeldungen nicht automatisch verarbeitet werden. Diese werden im CRM gekennzeichnet und müssten manuell verarbeitet werden.',
    image: '/assets/registration-processes/Maxi-Anmeldung.png'
  },
    'binding-registration':
  {
    title: 'Online-Vertragsabschluss [EXPERIMENTELL]',
    text: 'Rechtlich bindende Anmeldung mit AGB-Akzeptanz. Sind AGB inkl. Preise als Spezialseite hinterlegt können verbindliche Verträge direkt auf der Website geschlossen werden. Für die Rechtskonformität der Anmeldung ist der Franchise-Nehmer selbst verantwortlich und muss im Einzelfall von diesem geprüft werden.',
    image: '/assets/registration-processes/Online-Vertragsabschluss.png'
  }
  }

export type FeatureFlags = {
  INDIVIDUAL_LESSONS: boolean,
  SPECIAL_OPENING_TIMES: boolean,
  ANNOUNCEMENT_MODAL: boolean,
  ALLOW_ALL_THEORY_LOCATIONS: boolean,
}

export type FeatureFlag = keyof FeatureFlags;

export class Website {
  public id: string | undefined;
  public domain: string;
  public logoSlug: string;
  public name: string;
  public legalName: string;
  public areaProtectionZip: string[];
  public areasServed: string[];
  public bundeslaender: string[];
  public cities: string[];
  public maxiUrl: string;
  public fahrschulmanagerId: string;
  public fahrschulmanagerApiKey: string;
  private _registrationType: RegistrationType = 'lead-generation';

  public releaseChannel: ReleaseChannel;

  public featureFlags: FeatureFlags;

  public settings: WebsiteSettings;

  constructor(el: any = {}) {
    this.id = el.id;
    this.domain = el.domain ?? '';
    this.logoSlug = el.logoSlug || '';
    this.name = el.name || '';
    this.legalName = el.legalName || '';
    this.areaProtectionZip = el.areaProtectionZip || [];
    this.areasServed = el.areasServed || [];
    this.bundeslaender = el.bundeslaender || [];
    this.cities = el.cities || [];
    this.maxiUrl = el.maxiUrl || '';
    this.fahrschulmanagerId = el.fahrschulmanagerId || '';
    this.fahrschulmanagerApiKey = el.fahrschulmanagerApiKey || '';
    this.releaseChannel = el.releaseChannel || "STANDARD";
    this.featureFlags = el.featureFlags || {INDIVIDUAL_LESSONS: false};

    this.settings = new WebsiteSettings(el.settings);

    if (this.settings.requireAgbAcceptanceOnRegistration) {
      this.registrationType = 'binding-registration';
    } else if (this.maxiUrl) {
      this.registrationType = 'maxi-registration';
    } else {
      this.registrationType = 'lead-generation';
    }
  }

  set registrationType(type: RegistrationType) {
    this._registrationType = type;

    switch (type) {
      case 'lead-generation':
        this.settings.requireAgbAcceptanceOnRegistration = false;
        this.maxiUrl = '';
        break;
      case 'binding-registration':
        this.settings.requireAgbAcceptanceOnRegistration = true;
        this.maxiUrl = '';
        break;
      case 'maxi-registration':
        this.settings.requireAgbAcceptanceOnRegistration = false;
        break;
    }
  }

  get registrationType(): RegistrationType {
    return this._registrationType;
  }

  public toJson(): any {
    return {
      id: this.id,
      domain: this.domain,
      logoSlug: this.logoSlug,
      name: this.name,
      legalName: this.legalName,
      areaProtectionZip: this.areaProtectionZip,
      bundeslaender: this.bundeslaender,
      cities: this.cities,
      maxiUrl: this.maxiUrl,
      fahrschulmanagerId: this.fahrschulmanagerId,
      fahrschulmanagerApiKey: this.fahrschulmanagerApiKey,
      releaseChannel: this.releaseChannel,

      settings: this.settings.toJson(),
    };
  }
}

export const globalWebsite = new Website({
  id: null,
  domain: 'GLOBAL',
  name: 'Academy Franchise',
});
